import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  NgbModal,
  NgbModalRef,
  NgbTypeaheadSelectItemEvent,
} from "@ng-bootstrap/ng-bootstrap";

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { NoalGlobalData } from "../../../_services/eventsServices/events.service";
import { OperatorFunction } from "rxjs/interfaces";
import { Observable } from "rxjs/Observable";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { FormControl, FormGroup } from "@angular/forms";
import { Circle, County, Nest } from "../event-page-models/noal.models";
import { GenericModalComponent } from "../../../form/generic-app-modal/generic-modal.component";

export interface EventOrgan {
  id: number;
  name: string;
  code: string;
  parent: number;
  nestOrCounty: "nest" | "county";
  isChosen: boolean;
  has_add_permission: boolean;
}

export interface NestCollection {
  id: number;
  name: string;
  code: string;
  nestsIds: number[];
  isNestCollection: true;
  isChosen: boolean;
  has_add_permission: boolean;
  is_kehila: boolean;
}

const IS_MAAGAL_FILTERING_ACTIVE = false;
@Component({
  selector: "create-parent-event-form-pricing-list-nests",
  templateUrl: "./create-parent-event-form-pricing-nests.component.html",
  styleUrls: [
    "create-parent-event-form-pricing-nests.component.scss",
    "../create-parent-event-form-pricing-row/create-parent-event-form-pricing-row.component.scss",
  ],

  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          maxHeight: "2000px",
          overflow: "visible",
          opacity: 1,
        })
      ),
      state(
        "closed",
        style({
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("* => open", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class CreateParentEventFormPricingNestsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public nestsOpenClose: boolean[] = [];
  public allNests: Nest[];
  public allCounties: County[];
  public allOrgans: EventOrgan[];
  public activeCircles: Circle[] = [];
  public nestCollection: NestCollection[] = [];

  public selectedCounties: Array<County & { nests: Nest[] }> = [];
  @ViewChild("resultTemplate") public resultTemplate;
  @ViewChild("autoCompleteInput")
  public autoCompleteInput: ElementRef;
  public searchForm: FormGroup;
  public searchFieldName: string = "searchField";
  @Output("updateSelectedOrgans") public updateSelectedOrgans: EventEmitter<
    EventOrgan[]
  > = new EventEmitter<EventOrgan[]>();

  constructor(private modalService: NgbModal) {}

  @Input("noalGlobalData")
  public set noalGlobalData(value: NoalGlobalData) {
    this.setGlobalData(value);
  }

  @Input("chosenOrgans")
  public set chosenOrgans(value: EventOrgan[]) {
    this.updateGlobalData(value);
    value.forEach((o) => {
      if (o.nestOrCounty === "nest") {
        this.addOrganToCounties({ ...o, isChosen: false });
      }
    });
    this.updateOrganStatus();
  }

  public ngOnInit(): void {
    this.searchForm = new FormGroup({});
    this.searchForm.addControl(this.searchFieldName, new FormControl(""));
  }

  public searchHandler: OperatorFunction<string, any> = (
    text$: Observable<any>
  ) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(
        (term): Array<EventOrgan | NestCollection> =>
          term.length < 2
            ? []
            : [...this.nestCollection, ...this.allOrgans]
                .filter((o) => o.has_add_permission)
                .filter(
                  (v) => v.name.toLowerCase().indexOf(term.toLowerCase()) > -1
                )
                .slice(0, 10)
      )
    );

  public formatter = (x: { name: string }) => x.name;

  public ngAfterViewInit() {}

  public ngOnDestroy(): void {}

  submit() {}

  public getResultTemplate() {
    return this.resultTemplate;
  }

  public organSelected($event: NgbTypeaheadSelectItemEvent) {
    $event.preventDefault();
    this.searchForm.controls[this.searchFieldName].reset(null);
    if ($event.item.isNestCollection) {
      this.handleNestCollectionSelected($event.item as NestCollection);
      return;
    }
    const organSelected = $event.item as EventOrgan;
    this.addOrganToCounties(organSelected);
  }
  public handleNestCollectionSelected(nestCollection: NestCollection) {
    try {
      let allNestsInCollection: Nest[] = this.allNests.filter((n) =>
        nestCollection.nestsIds.includes(n.nestId)
      );

      let allCountiesIdsInCollection = new Set(
        allNestsInCollection.map((n) => n.parent)
      );

      allCountiesIdsInCollection.forEach((countyId) => {
        const countyFromCurrentList = this.selectedCounties.find(
          (c) => countyId === c.countyId
        );

        const county: County & { nests?: Nest[] } = countyFromCurrentList || {
          ...this.allCounties.find((c) => c.countyId === countyId),
        };
        county.nests = county.nests || [];
        let nestsToAddToCounty: Nest[] = this.allNests.filter(
          (n) =>
            nestCollection.nestsIds.includes(n.nestId) &&
            !county.nests
              .map((existingNests) => existingNests.nestId)
              .includes(n.nestId) &&
            n.parent === county.countyId &&
            n.has_add_permission
        );
        county.nests.push(...nestsToAddToCounty);
        if (!countyFromCurrentList && county.nests.length) {
          this.selectedCounties.push({
            ...(county as County & { nests: Nest[] }),
          });
        }
      });

      this.nestsOpenClose = [];
      this.updateOrganStatus();
    } catch (e) {
      const modalRef: NgbModalRef = this.modalService.open(
        GenericModalComponent
      );
      modalRef.componentInstance.modalOptions = {
        approveOption: "אישור",
        cancelOption: "",
        modalHeader: "שגיאה",
        modalText: `חלה שגיאה בהוספת  ${nestCollection.name} . אנא פנה לתמיכה. `,
        onlyApprove: true,
      };

      return;
    }
  }

  public removeOrgan(countyIndex: number) {
    this.selectedCounties.splice(countyIndex, 1);

    this.updateOrganStatus();
    this.nestsOpenClose = [];
  }

  public removeNestFromOrgan(countyIndex: number, nestIndex: number) {
    const county = this.selectedCounties[countyIndex];
    if (county.nests.length === 1) {
      this.selectedCounties.splice(countyIndex, 1);
      this.updateOrganStatus();
      return;
    }
    county.nests.splice(nestIndex, 1);
    this.updateOrganStatus();
  }

  public toggleNestsOpenClose(i: number) {
    this.nestsOpenClose[i] = !this.nestsOpenClose[i];
  }

  public disableClick($event, stop: boolean) {
    if (!stop) {
      return;
    }
    $event.stopPropagation();
    $event.stopImmediatePropagation();
  }

  private isIncludedInCircleChoice(organ: EventOrgan) {
    if (!IS_MAAGAL_FILTERING_ACTIVE) {
      return true;
    }
    let countyParent = organ.parent;
    if (organ.nestOrCounty === "nest") {
      const county = this.allCounties.find((c) => c.countyId === organ.parent);
      countyParent = county.parent;
    }

    return this.activeCircles.map((c) => c.circleId).includes(countyParent);
  }

  private updateOrganStatus() {
    const allChosenNestsIds = [].concat(
      ...this.selectedCounties.map((c) => c.nests.map((n) => n.nestId))
    );

    this.allOrgans
      .filter((o) => o.nestOrCounty === "nest")
      .forEach((nest) => {
        nest.isChosen = allChosenNestsIds.includes(nest.id);
      });

    this.updateSelectedOrgans.emit(this.allOrgans.filter((o) => o.isChosen));
  }

  private getNestsByCountyId(countyId: number) {
    return this.allNests.filter((n) => n.parent === countyId);
  }

  private eventOrganToNest(organ: EventOrgan): Nest {
    return {
      nestId: organ.id,
      name: organ.name,
      code: organ.code,
      parent: organ.parent,
      has_add_permission: true,
    };
  }

  private addOrganToCounties(eventOrgan: EventOrgan) {
    if (eventOrgan.isChosen) {
      return;
    }

    const countyId =
      eventOrgan.nestOrCounty === "nest" ? eventOrgan.parent : eventOrgan.id;

    const countFromCurrentList = this.selectedCounties.find(
      (c) => c.countyId === countyId
    );
    const county: County & { nests?: Nest[] } = countFromCurrentList || {
      ...this.allCounties.find((c) => c.countyId === countyId),
    };

    let nestsToAdd: Nest[] =
      eventOrgan.nestOrCounty === "nest"
        ? [this.eventOrganToNest(eventOrgan)]
        : this.getNestsByCountyId(countyId);

    county.nests = county.nests || [];
    nestsToAdd = nestsToAdd.filter(
      (n) =>
        !county.nests
          .map((existingNests) => existingNests.nestId)
          .includes(n.nestId)
    );
    county.nests.push(...nestsToAdd);

    if (!countFromCurrentList) {
      this.selectedCounties.push({
        ...(county as County & { nests: Nest[] }),
      });
    }
    this.nestsOpenClose = [];
    this.updateOrganStatus();
  }
  private updateGlobalData(organsToAdd: EventOrgan[]) {
    const existingOrgans = this.allOrgans.map(
      (o) => `${o.nestOrCounty}_${o.id}`
    );
    const organsWithoutPermission = organsToAdd.filter(
      (o) => !existingOrgans.includes(`${o.nestOrCounty}_${o.id}`)
    );

    this.allOrgans.push(...organsWithoutPermission);

    this.allCounties.push(
      ...organsWithoutPermission
        .filter((o) => o.nestOrCounty === "county")
        .map((o) => {
          return {
            countyId: o.id,
            name: o.name,
            code: o.code,
            parent: o.parent,
            has_add_permission: o.has_add_permission,
          };
        })
    );

    this.allNests.push(
      ...organsWithoutPermission
        .filter((o) => o.nestOrCounty === "nest")
        .map((o) => {
          return {
            nestId: o.id,
            name: o.name,
            code: o.code,
            parent: o.parent,
            has_add_permission: o.has_add_permission,
          };
        })
    );
  }
  private setGlobalData(noalData: NoalGlobalData) {
    this.allCounties = noalData.counties.map((c) => ({ ...c }));

    this.allNests = noalData.nests.map((n) => ({ ...n }));
    this.allOrgans = [];

    const nestsAsOrgans: EventOrgan[] = this.allNests.map((nest) => ({
      code: nest.code,
      id: nest.nestId,
      isChosen: false,
      name: nest.name,
      nestOrCounty: "nest" as "nest",
      parent: nest.parent,
      has_add_permission: nest.has_add_permission,
    }));
    const countiesAsOrgans: EventOrgan[] = this.allCounties.map((county) => ({
      code: county.code,
      id: county.countyId,
      isChosen: false,
      name: county.name,
      nestOrCounty: "county" as "county",
      parent: county.parent,
      has_add_permission: county.has_add_permission,
    }));
    this.allOrgans = [...countiesAsOrgans, ...nestsAsOrgans];
    this.nestCollection = noalData.ken_collections.map((collection) => {
      const oneNestCollection: NestCollection = {
        code: collection.id.toString(),
        has_add_permission: true,
        id: collection.id,
        isChosen: false,
        isNestCollection: true,
        name: collection.name,
        nestsIds: collection.kenim,
        is_kehila: collection.is_kehila,
      };
      return oneNestCollection;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/events-screen/create-parent-event-form-pricing-nests/create-parent-event-form-pricing-nests.component.ts